import { useState, useMemo } from 'react';
import { FiChevronUp } from 'react-icons/fi';
import moment from 'moment';
import { useTranslation } from 'next-i18next';
import Accordion from '../common/Accordion/Accordion';
import { FAQByLocale } from '../../../types/Faq';
import { COMMON_DATE_FORMAT } from '../../core/constants';
import Markdown from '../common/Markdown/Markdown';
import analytics from '../../core/analytics/analytics';
import SkipTranslationWrapper from '../common/SkipTranslationWrapper';

type Props = {
  hotelId?: string;
  faq: FAQByLocale;
};

const FAQAccordion = ({ hotelId, faq }: Props) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('hotel');

  const button = (
    <button
      type="button"
      className="relative flex w-full items-center justify-between px-1 py-3"
      onClick={() => {
        if (!open) {
          // track event only when the accordion is opened
          analytics.google.ecommerce.track.viewFaq('faq', faq.question, faq.id, hotelId);
        }
        return setOpen(!open);
      }}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex gap-2 text-left text-gray-600">
          <div className="flex size-6 flex-none justify-center rounded-sm bg-gray-50">Q</div>
          <p className="whitespace-pre-wrap text-base">{faq.question}</p>
        </div>

        <p
          className={`${
            open ? 'ease duration-200' : 'ease rotate-180 duration-200'
          } ml-3 inline-block`}
        >
          <FiChevronUp size="24" className="text-orange-700" />
        </p>
      </div>
    </button>
  );

  const content = useMemo(() => {
    return (
      <div className="mb-3 flex w-full flex-col gap-2 rounded-lg bg-gray-50 p-4 text-gray-600">
        <Markdown className="whitespace-pre-wrap">{faq.answer}</Markdown>
        <p className="text-xs">
          {t('hotel:faq.last_modified')}
          <SkipTranslationWrapper tag="span">
            ：{moment(faq.updatedAt).format(COMMON_DATE_FORMAT)}
          </SkipTranslationWrapper>
        </p>
      </div>
    );
  }, [faq.answer, faq.updatedAt, t]);

  return (
    <Accordion
      button={button}
      content={content}
      borderColor="none"
      open={open}
      direction="bottom"
    />
  );
};

export default FAQAccordion;
